// pergolaParams.ts — the parameter set the wizard's parametric branch collects
// for a fixed-slat pergola, and its validation.
//
// This is the portal-side mirror of `PergolaDimensions` in
// process-parametric-geometry/pergola-recipe.ts. It is a hand-kept mirror
// rather than a shared import because the portal is a browser bundle and that
// package is a Lambda dependency; `toPergolaDimensions` is the single crossing
// point, so a drift shows up there rather than scattered through the form.
//
// ⚠️ Non-negotiable 3 applies: do NOT reimplement the generator here to preview
// geometry in the browser. These are inputs only. Preview comes from the
// existing generate-parametric-preview Edge Function, which runs the real
// generator — one source of truth.
//
// Defaults are the M3a golden 12x16 fixture, the only parameter set with a
// committed golden OBJ hash and a baked shadow atlas.

export interface PergolaParams {
  spanWidthFt: number;
  spanDepthFt: number;
  postSectionMm: number;
  beamWidthMm: number;
  beamHeightMm: number;
  rafterWidthMm: number;
  rafterHeightMm: number;
  rafterSpacingMm: number;
  slatWidthMm: number;
  slatHeightMm: number;
  slatGapRatio: number;
  heightMm: number;
  overhangMm: number;
}

/** The generator's input shape. Mirrors PergolaDimensions. */
export interface PergolaDimensionsPayload {
  spanWidthFt: number;
  spanDepthFt: number;
  postSectionMm: number;
  beamSectionMm: [number, number];
  rafterSectionMm: [number, number];
  rafterSpacingMm: number;
  slatSectionMm: [number, number];
  slatGapRatio: number;
  heightMm: number;
  overhangMm: number;
}

export const DEFAULT_PERGOLA_PARAMS: PergolaParams = {
  spanWidthFt: 12,
  spanDepthFt: 16,
  postSectionMm: 100,
  beamWidthMm: 100,
  beamHeightMm: 150,
  rafterWidthMm: 50,
  rafterHeightMm: 150,
  rafterSpacingMm: 600,
  slatWidthMm: 50,
  slatHeightMm: 25,
  slatGapRatio: 0.35,
  heightMm: 2440,
  overhangMm: 200,
};

export interface PergolaParamField {
  key: keyof PergolaParams;
  label: string;
  unit: string;
  step?: number;
  /** Shown under the input — these numbers are not self-explanatory. */
  hint?: string;
  group: "Span" | "Sections" | "Slats";
}

export const PERGOLA_PARAM_FIELDS: PergolaParamField[] = [
  { key: "spanWidthFt", label: "Span width", unit: "ft", step: 0.5, group: "Span",
    hint: "Post centre to post centre, across the front." },
  { key: "spanDepthFt", label: "Span depth", unit: "ft", step: 0.5, group: "Span",
    hint: "Post centre to post centre, front to back." },
  { key: "heightMm", label: "Post height", unit: "mm", group: "Span",
    hint: "Ground to top of post, before beams and rafters." },
  { key: "overhangMm", label: "Rafter overhang", unit: "mm", group: "Span",
    hint: "Rafter tail past the beam at each end. 0 for a flush end." },

  { key: "postSectionMm", label: "Post section", unit: "mm", group: "Sections",
    hint: "Square." },
  { key: "beamWidthMm", label: "Beam width", unit: "mm", group: "Sections" },
  { key: "beamHeightMm", label: "Beam height", unit: "mm", group: "Sections" },
  { key: "rafterWidthMm", label: "Rafter width", unit: "mm", group: "Sections" },
  { key: "rafterHeightMm", label: "Rafter height", unit: "mm", group: "Sections" },
  { key: "rafterSpacingMm", label: "Rafter spacing", unit: "mm", group: "Sections",
    hint: "Centre to centre. Rafter count is derived from this and the span." },

  { key: "slatWidthMm", label: "Slat width", unit: "mm", group: "Slats" },
  { key: "slatHeightMm", label: "Slat height", unit: "mm", group: "Slats",
    hint: "Depth of the slat. At low sun the slat EDGE is what closes the gaps, so this drives shade as much as width does." },
  { key: "slatGapRatio", label: "Open-sky ratio", unit: "", step: 0.01, group: "Slats",
    hint: "Gap ÷ centre spacing, 0–1. 0.35 ≈ 65% shade at high sun. Slat count is derived from this." },
];

export type PergolaParamErrors = Partial<Record<keyof PergolaParams, string>>;

const FT_TO_MM = 304.8;

/**
 * Field-level validation.
 *
 * Mirrors the throws in pergolaFixedSlatGroups and buildSlatArray so a bad
 * value surfaces next to the input rather than as a failed Lambda invocation
 * an admin has to find in CloudWatch.
 */
export function validatePergolaParams(p: PergolaParams): PergolaParamErrors {
  const errors: PergolaParamErrors = {};

  const positive: (keyof PergolaParams)[] = [
    "spanWidthFt", "spanDepthFt", "heightMm", "postSectionMm",
    "beamWidthMm", "beamHeightMm", "rafterWidthMm", "rafterHeightMm",
    "rafterSpacingMm", "slatWidthMm", "slatHeightMm",
  ];
  for (const key of positive) {
    const v = p[key];
    if (!Number.isFinite(v)) errors[key] = "Required";
    else if (v <= 0) errors[key] = "Must be greater than 0";
  }

  if (!Number.isFinite(p.overhangMm)) errors.overhangMm = "Required";
  else if (p.overhangMm < 0) errors.overhangMm = "Cannot be negative";

  if (!Number.isFinite(p.slatGapRatio)) errors.slatGapRatio = "Required";
  else if (p.slatGapRatio < 0 || p.slatGapRatio >= 1) {
    errors.slatGapRatio = "Must be at least 0 and less than 1";
  }

  // resolveCount is floor(span/spacing)+1, so a spacing wider than the span
  // quietly yields two rafters instead of failing. Catch it as a typo.
  if (!errors.rafterSpacingMm && !errors.spanWidthFt) {
    const spanMm = p.spanWidthFt * FT_TO_MM;
    if (p.rafterSpacingMm > spanMm) {
      errors.rafterSpacingMm = `Wider than the ${Math.round(spanMm)} mm span — you would get two rafters`;
    }
  }

  return errors;
}

/** Converts the flat form shape into the generator's input. */
export function toPergolaDimensions(p: PergolaParams): PergolaDimensionsPayload {
  return {
    spanWidthFt:     p.spanWidthFt,
    spanDepthFt:     p.spanDepthFt,
    postSectionMm:   p.postSectionMm,
    beamSectionMm:   [p.beamWidthMm, p.beamHeightMm],
    rafterSectionMm: [p.rafterWidthMm, p.rafterHeightMm],
    rafterSpacingMm: p.rafterSpacingMm,
    slatSectionMm:   [p.slatWidthMm, p.slatHeightMm],
    slatGapRatio:    p.slatGapRatio,
    heightMm:        p.heightMm,
    overhangMm:      p.overhangMm,
  };
}
